import { useFormik } from 'formik';

import * as yup from 'yup';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';

import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import { logIn } from 'api';

import RouterLink from 'components/RouterLink/RouterLink';
import { useSetRecoilState } from 'recoil';
import { ToastSeverity, toastStateSelector } from 'state';
import { ModelTypes, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';
import { query } from 'operations';
import { Project } from 'api/models';

export default function Login() {
  const { t } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);
  const navigate = useNavigate();

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(t('login.errors.email') ?? '')
      .required(t('login.errors.mandatory') ?? ''),
    password: yup.string().required(t('login.errors.mandatory') ?? ''),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async values => {
      const { email, password } = values;
      try {
        const response = await logIn(email, password);

        if (response.status === 200) {
          const { data: projects } = await query<Project[]>({
            type: ModelTypes.project,
          });

          if (projects?.length === 1) {
            navigate(
              getRoutePath(RoutePaths.projectDashboard, {
                projectId: projects[0]?.id,
              }),
            );
          } else {
            navigate(RoutePaths.projectSelector);
          }
        } else {
          setToast({
            isOpen: true,
            message: t('login.errors.loginFailed'),
            severity: ToastSeverity.error,
          });
        }
      } catch (error) {
        setToast({
          isOpen: true,
          message: t('login.errors.loginFailed'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  const token = localStorage.getItem('token');

  if (token) {
    // if token is defined redirect to project selector
    window.location.href = RoutePaths.projectSelector;
    return <></>;
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ minHeight: '100vh' }}
    >
      <Paper elevation={3}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            p={3}
            minWidth="500px"
          >
            <TextField
              fullWidth
              margin="normal"
              id="login-email"
              inputProps={{ 'data-testid': 'login-email-input' }}
              name="email"
              label={t('login.email')}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              fullWidth
              margin="normal"
              id="login-password"
              inputProps={{ 'data-testid': 'login-password-input' }}
              name="password"
              label={t('login.password')}
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <Box>
              <RouterLink to="/password-reset">
                {t('login.forgottenPassword')}
              </RouterLink>
            </Box>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              sx={{ marginTop: '20px' }}
              data-testid="login-submit-button"
            >
              {t('login.login')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
